import styled from "@emotion/styled";
import { useState } from "react";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Button,
  Checkbox,
  FormField,
  Modal,
  ModalPortal,
  SpacingToken,
  Text,
  Textarea,
} from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import {
  IApplication,
  IWithApplicationsState,
  isInReviewApplication,
  isPrivateApplication,
} from "store/entities/applications";
import { ApplicationsActions } from "store/features/applications/actions";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

import { getProperListUrlForApp } from "../helpers";

interface IProps {
  application: IApplication;
  shouldRedirect?: boolean;
  onClose: () => void;
}

const otherReason = "Other reason.";
const allReasons = [
  "There were technical issues.",
  "This app didn't work as expected.",
  "Our team no longer needs this app.",
  "This app is too expensive.",
  otherReason,
];

const UninstallModal: React.FC<IProps> = ({ onClose, shouldRedirect, application }) => {
  const dispatch = useDispatch();
  const isPrivateApp = useSelector((state: IWithApplicationsState) => isPrivateApplication(state, application?.id));
  const isInReviewApp = useSelector((state: IWithApplicationsState) => isInReviewApplication(state, application?.id));

  const [pickedReasons, setPickedReasons] = useState(new Set<string>([]));
  const [otherReasonValue, setOtherReasonValue] = useState("");

  const isOtherReasonSelected = pickedReasons.has(otherReason);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleReasonPick = (pickedReason: string) => {
    if (pickedReasons.has(pickedReason)) {
      setPickedReasons(prev => {
        const updatedReasons = new Set(prev);
        updatedReasons.delete(pickedReason);
        return updatedReasons;
      });
    } else {
      setPickedReasons(prev => new Set(prev).add(pickedReason));
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const getReasonsSummary = () =>
    [...pickedReasons].reduce((summary: string, reason: string, index: number) => {
      const divider: string = index === 0 ? "" : ", ";

      if (reason === otherReason) {
        return `${summary}${divider}Other reason: ${otherReasonValue}`;
      }

      return `${summary}${divider}${reason}`;
    }, "");

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const getRedirectTo = () => {
    if (!shouldRedirect) {
      return null;
    }

    return getProperListUrlForApp(isPrivateApp, isInReviewApp);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleSubmit = () => {
    const reasons = getReasonsSummary();
    const redirectTo = getRedirectTo();

    // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
    dispatch(ApplicationsActions.uninstallApplication(application?.id, redirectTo, reasons));
    onClose();
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setOtherReasonValue(e.target.value);
  };

  const applicationName = application?.name || "Application";

  return (
    <ModalPortal zIndex={StackingContextLevel.Modal}>
      <Modal
        onClose={onClose}
        heading={`Uninstalling ${applicationName}`}
        footer={
          <Button kind="destructive" onClick={handleSubmit}>
            Submit and uninstall
          </Button>
        }
        css={{ maxWidth: "600px" }}
      >
        <Text bold>
          Please tell us why are you uninstalling {applicationName}. We&apos;ll use your feedback to improve the
          Marketplace experience.
        </Text>

        <Flex column gap={withVar(SpacingToken.Spacing2)} marginTop={withVar(SpacingToken.Spacing2)}>
          {allReasons.map(reason => {
            const isChecked = pickedReasons.has(reason);

            return (
              <Checkbox key={reason} checked={isChecked} onChange={() => handleReasonPick(reason)}>
                {reason}
              </Checkbox>
            );
          })}

          {isOtherReasonSelected && (
            <FormField labelText="Uninstalling reason">
              <OtherTextArea
                id="uninstall-app-other-reason"
                value={otherReasonValue}
                placeholder="Enter the reason for uninstalling the app"
                onChange={handleTextAreaChange}
              />
            </FormField>
          )}
        </Flex>
      </Modal>
    </ModalPortal>
  );
};

const OtherTextArea = styled(Textarea)`
  width: 100%;
  textarea {
    resize: none;
    min-height: 80px;
    width: 100%;
  }
`;

export default UninstallModal;
